import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { firestore } from "../firebase";
import useAuth from "../hooks/useAuth";
import Modal from "./Modal";
import { useModal } from "../hooks/useModal";
// import { filterLeadsWithAI } from "../utils/openaiFilter"; // AI filtering disabled
import { FiMapPin, FiPhone, FiGlobe, FiDownload, FiSearch, FiX, FiCheck, FiList, FiChevronLeft, FiChevronRight, FiFilter, FiZap, FiXCircle } from "react-icons/fi";
import { 
  getCachedSearchResults, 
  cacheSearchResults, 
  getCachedPlaceDetails, 
  cachePlaceDetails 
} from "../utils/placesCache";
import { 
  textSearchNew, 
  getPlaceDetailsNew, 
  formatPlaceDataNew,
  autocompleteNew
} from "../utils/placesApiNew";
import "../styles/quotation-system.css";

export default function FindLeadsOnMaps() {
  const history = useHistory();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [allLeads, setAllLeads] = useState([]); // All fetched leads
  const [displayedLeads, setDisplayedLeads] = useState([]); // Leads for current page
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [contactedLeads, setContactedLeads] = useState(new Set());
  const [shortlistedLeadIds, setShortlistedLeadIds] = useState(new Set());
  const [leadsWithQuotes, setLeadsWithQuotes] = useState(new Set()); // Track leads that have quotes generated
  const [disqualifiedLeadIds, setDisqualifiedLeadIds] = useState(new Set()); // Track disqualified leads
  const [notification, setNotification] = useState(null);
  const { modal, showAlert, showConfirm, hideModal } = useModal();
  const [activeFilter, setActiveFilter] = useState("default");
  // const [aiFiltering, setAiFiltering] = useState(false); // AI filtering disabled
  // const [apiUsage, setApiUsage] = useState(null); // AI filtering disabled
  // const [aiFilterCache, setAiFilterCache] = useState(new Map()); // AI filtering disabled
  const [filteredLeadsCount, setFilteredLeadsCount] = useState(0); // Track total filtered leads count

  // Show custom notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };
  const [placesService, setPlacesService] = useState(null);
  const [geocoder, setGeocoder] = useState(null);
  const [mapsReady, setMapsReady] = useState(false);
  const [isAreaSearch, setIsAreaSearch] = useState(false);
  const [searchLocation, setSearchLocation] = useState(null);
  const scrollPositionRef = useRef(0);
  const searchStateRef = useRef({ query: '', results: [], page: 1 });
  const autocompleteInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const autocompleteTimeoutRef = useRef(null);
  const updateTimeoutRef = useRef(null);
  const pendingResultsRef = useRef([]);
  const allFetchedResultsRef = useRef([]);
  const searchRadiusRef = useRef(2000); // Start with 2km for area searches
  const containerRef = useRef(null);

  // Initialize Google Maps services
  useEffect(() => {
    const initGoogleMaps = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        try {
          setPlacesService(new window.google.maps.places.PlacesService(document.createElement('div')));
          setGeocoder(new window.google.maps.Geocoder());
          setMapsReady(true);
          setError(null);
        } catch (error) {
          console.error("Error initializing Google Maps services:", error);
          setError("Failed to initialize Google Maps. Please refresh the page.");
        }
      }
    };

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      initGoogleMaps();
    } else {
      // Set up callback for when Google Maps loads
      window.onGoogleMapsLoaded = () => {
        initGoogleMaps();
      };
      
      // Poll for Google Maps to be available (fallback)
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          initGoogleMaps();
          clearInterval(checkInterval);
        }
      }, 200);

      // Cleanup interval after 15 seconds
      const timeout = setTimeout(() => {
        clearInterval(checkInterval);
        if (!window.google || !window.google.maps || !window.google.maps.places) {
          setError("Google Maps failed to load. Please check your internet connection and refresh the page.");
        }
      }, 15000);

      return () => {
        clearInterval(checkInterval);
        clearTimeout(timeout);
      };
    }
  }, []);

  // Handle place selection from autocomplete suggestions
  const handleSuggestionSelect = async (suggestion) => {
    setSearchQuery(suggestion.text);
    setShowSuggestions(false);
    setAutocompleteSuggestions([]);
    
    if (suggestion.placeId) {
      // Get place details using Places API (New)
      try {
        const placeDetails = await getPlaceDetailsNew(suggestion.placeId);
        
        // Format place details to match expected structure
        const place = {
          id: placeDetails.id,
          place_id: placeDetails.id,
          name: placeDetails.displayName?.text || placeDetails.displayName || suggestion.text,
          formatted_address: placeDetails.formattedAddress || suggestion.description,
          geometry: placeDetails.location ? {
            location: {
              lat: () => placeDetails.location.latitude,
              lng: () => placeDetails.location.longitude
            }
          } : null,
          types: placeDetails.types || []
        };
        
        await handlePlaceSelected(place);
      } catch (error) {
        console.error('Error fetching place details:', error);
        setError("Could not fetch details for this location. Please try again.");
      }
    }
  };

  // Handle place selection (from autocomplete or manual search)
  const handlePlaceSelected = async (place) => {
    if (place.place_id || place.id) {
      await fetchPlaceDetails(place.place_id || place.id, place.geometry?.location);
    }
  };

  // Handle autocomplete input with debouncing (using Places API New)
  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setAutocompleteSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Clear previous timeout
    if (autocompleteTimeoutRef.current) {
      clearTimeout(autocompleteTimeoutRef.current);
    }

    // Debounce autocomplete requests (wait 300ms after user stops typing)
    autocompleteTimeoutRef.current = setTimeout(async () => {
      try {
        const suggestions = await autocompleteNew({ 
          input: searchQuery,
          languageCode: 'en'
        });
        setAutocompleteSuggestions(suggestions);
        // Don't automatically show suggestions - only show when user focuses on input
        // setShowSuggestions(suggestions.length > 0);
      } catch (error) {
        console.error('Error fetching autocomplete suggestions:', error);
        setAutocompleteSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => {
      if (autocompleteTimeoutRef.current) {
        clearTimeout(autocompleteTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Handle search input change (just update state, Google Autocomplete handles suggestions)
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setError(null);
  };

  // Fetch place details with caching (using Places API New)
  const fetchPlaceDetails = async (placeId, location = null) => {
    // Check cache first
    const cachedDetails = getCachedPlaceDetails(placeId);
    if (cachedDetails) {
      const place = cachedDetails;
      // Check if it's a specific business or a location (legacy format)
      const isSpecificBusiness = place.types && place.types.some(type => 
        type === 'establishment' || type === 'store' || type === 'point_of_interest'
      );

      if (isSpecificBusiness) {
        const lead = formatLeadData(place);
        if (lead) {
          setAllLeads([lead]);
          setCurrentPage(1);
        } else {
          setError("This business doesn't have contact information (phone or website).");
        }
      } else {
        // For location searches, we need geometry.location
        const placeLocation = place.geometry?.location || place.location;
        if (placeLocation) {
          const lat = typeof placeLocation.lat === 'function' ? placeLocation.lat() : placeLocation.lat || placeLocation.latitude;
          const lng = typeof placeLocation.lng === 'function' ? placeLocation.lng() : placeLocation.lng || placeLocation.longitude;
          
          if (lat && lng) {
            const addressComponents = place.address_components || [];
            const hasLocality = addressComponents.some(ac => 
              ac.types.includes('locality') || ac.types.includes('sublocality') || 
              ac.types.includes('sublocality_level_1') || ac.types.includes('neighborhood')
            );
            setIsAreaSearch(hasLocality);
            setSearchLocation({ lat: () => lat, lng: () => lng });
            await searchNearbyBusinesses({ lat: () => lat, lng: () => lng }, hasLocality);
          }
        }
      }
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use Places API (New) to get place details
      const placeDetails = await getPlaceDetailsNew(placeId);
      
      // Cache the place details
      cachePlaceDetails(placeId, placeDetails);

      // Check if it's a specific business or a location
      // For Places API (New), we need to check if it has phone number (business) or not (location)
      const hasPhone = placeDetails.nationalPhoneNumber || placeDetails.internationalPhoneNumber;
      
      if (hasPhone) {
        // Single business result
        const lead = formatPlaceDataNew(placeDetails);
        if (lead) {
          setAllLeads([lead]);
          setCurrentPage(1);
        } else {
          setError("This business doesn't have contact information (phone or website).");
        }
      } else {
        // Location search - find nearby businesses
        // Get location from placeDetails
        if (placeDetails.location) {
          const lat = placeDetails.location.latitude;
          const lng = placeDetails.location.longitude;
          
          // For location searches, assume it's an area if it has formattedAddress
          const hasLocality = !!placeDetails.formattedAddress;
          setIsAreaSearch(hasLocality);
          setSearchLocation({ lat: () => lat, lng: () => lng });
          await searchNearbyBusinesses({ lat: () => lat, lng: () => lng }, hasLocality);
        } else {
          setError("Could not determine location. Please try a different search.");
        }
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching place details:", err);
      setError("An error occurred while fetching place details. Please try again.");
      setLoading(false);
    }
  };

  // Search nearby businesses for solar leads using Places API (New)
  const searchNearbyBusinesses = async (location, isArea = false) => {
    if (!location) return;
    
    const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
    const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
    const currentRadius = isArea ? searchRadiusRef.current : 10000;
    
    // Generate cache key (used throughout the function)
    const cacheKey = `search_${lat.toFixed(4)}_${lng.toFixed(4)}_${currentRadius}`;
    
    // Check cache first (only if we don't have existing leads - i.e., initial search)
    if (allFetchedResultsRef.current.length === 0) {
      const cachedResults = getCachedSearchResults(location, cacheKey, currentRadius);
      if (cachedResults && cachedResults.length > 0) {
        setAllLeads(cachedResults);
        allFetchedResultsRef.current = cachedResults;
        setLoading(false);
        return;
      }
    }
    
    // Clear any pending updates
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
      updateTimeoutRef.current = null;
    }
    pendingResultsRef.current = [];

    const results = [];
    const currentRadiusValue = isArea ? searchRadiusRef.current : 10000; // Start small for areas, larger for cities
    
    // Optimized search queries for solar rooftop system leads
    // Focus on commercial establishments with large rooftops
    const searchQueries = [
      'warehouse',
      'factory',
      'manufacturing unit',
      'industrial building',
      'commercial building',
      'office building',
      'office complex',
      'business park',
      'mall',
      'shopping center',
      'shopping mall',
      'supermarket',
      'hypermarket',
      'hotel',
      'resort',
      'restaurant',
      'hospital',
      'clinic',
      'school',
      'college',
      'university',
      'educational institution',
      'cold storage',
      'godown',
      'storage facility',
      'logistics',
      'distribution center',
      'showroom',
      'automobile showroom',
      'car dealership',
      'apartment complex',
      'residential complex',
      'society',
      'cooperative housing society',
      // Fuel stations (for when user wants to find them)
      'petrol pump',
      'gas station',
      'fuel station',
      'petrol bunk',
      'indian oil',
      'hpcl',
      'bharat petroleum',
      'bpcl',
      'reliance petrol',
      'shell petrol',
      'nayara petrol'
    ];

    const completedSearchesRef = { current: 0 };
    const totalSearches = searchQueries.length;
    const processedPlaceIds = new Set();

    // Priority types for solar installations (higher priority = better leads)
    const priorityTypes = {
      'warehouse': 10,
      'factory': 10,
      'manufacturing': 10,
      'industrial': 10,
      'cold_storage': 9,
      'storage': 9,
      'godown': 9,
      'mall': 8,
      'shopping_center': 8,
      'shopping_mall': 8,
      'supermarket': 8,
      'hotel': 8,
      'office': 7,
      'commercial': 7,
      'hospital': 7,
      'school': 7,
      'college': 7,
      'university': 7,
      'apartment': 6,
      'residential': 6,
      'restaurant': 5,
      'store': 5
    };

    const getPriority = (name) => {
      if (!name) return 0;
      const nameLower = name.toLowerCase();
      let maxPriority = 0;
      Object.keys(priorityTypes).forEach(key => {
        if (nameLower.includes(key)) {
          maxPriority = Math.max(maxPriority, priorityTypes[key]);
        }
      });
      return maxPriority;
    };

    // Process search results using Places API (New)
    const processSearchResult = async (query, radius = currentRadiusValue) => {
      try {
        const places = await textSearchNew({
          textQuery: query,
          location: location,
          radius: radius
        });

        completedSearchesRef.current++;

        if (places && places.length > 0) {
          for (const place of places) {
            // Check if already processed
            if (place.id && !processedPlaceIds.has(place.id)) {
              processedPlaceIds.add(place.id);

              // Check cache first
              const cachedDetails = getCachedPlaceDetails(place.id);
              if (cachedDetails) {
                const lead = formatPlaceDataNew(cachedDetails);
                if (!lead) continue;

                lead.priority = getPriority(lead.name);

                // Avoid duplicates
                if (!results.find(r => r.placeId === lead.placeId)) {
                  results.push(lead);
                  allFetchedResultsRef.current.push(lead);
                  pendingResultsRef.current = [...allFetchedResultsRef.current];

                  // Batch updates to prevent glitchy scrolling
                  if (updateTimeoutRef.current) {
                    clearTimeout(updateTimeoutRef.current);
                  }

                  updateTimeoutRef.current = setTimeout(() => {
                    // Deduplicate by placeId before sorting
                    const uniqueLeads = [];
                    const seenPlaceIds = new Set();
                    for (const lead of pendingResultsRef.current) {
                      if (lead.placeId && !seenPlaceIds.has(lead.placeId)) {
                        seenPlaceIds.add(lead.placeId);
                        uniqueLeads.push(lead);
                      } else if (!lead.placeId) {
                        uniqueLeads.push(lead);
                      }
                    }

                    // Sort by priority (highest first)
                    const sorted = uniqueLeads.sort((a, b) => {
                      return (b.priority || 0) - (a.priority || 0);
                    });

                    setAllLeads(sorted);
                  }, 800);
                }
                continue;
              }

              // Format directly from text search result (already has name and phone)
              const lead = formatPlaceDataNew(place);
              if (!lead) continue;

              lead.priority = getPriority(lead.name);

              // Cache the place data
              cachePlaceDetails(place.id, place);

              // Avoid duplicates
              if (!results.find(r => r.placeId === lead.placeId)) {
                results.push(lead);
                allFetchedResultsRef.current.push(lead);
                pendingResultsRef.current = [...allFetchedResultsRef.current];

                // Batch updates to prevent glitchy scrolling
                if (updateTimeoutRef.current) {
                  clearTimeout(updateTimeoutRef.current);
                }

                updateTimeoutRef.current = setTimeout(() => {
                  // Deduplicate by placeId before sorting
                  const uniqueLeads = [];
                  const seenPlaceIds = new Set();
                  for (const lead of pendingResultsRef.current) {
                    if (lead.placeId && !seenPlaceIds.has(lead.placeId)) {
                      seenPlaceIds.add(lead.placeId);
                      uniqueLeads.push(lead);
                    } else if (!lead.placeId) {
                      uniqueLeads.push(lead);
                    }
                  }

                  // Sort by priority (highest first)
                  const sorted = uniqueLeads.sort((a, b) => {
                    return (b.priority || 0) - (a.priority || 0);
                  });

                  setAllLeads(sorted);
                }, 800);
              }
            }
          }
        }

        // When all searches complete, check if we need to expand radius (for area searches)
        if (completedSearchesRef.current === totalSearches) {
          // Clear any pending timeout
          if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
          }

          // For area searches: if we have less than 50 results, expand radius
          if (isArea && allFetchedResultsRef.current.length < 50 && searchRadiusRef.current < 10000) {
            searchRadiusRef.current = Math.min(searchRadiusRef.current * 2, 10000); // Double radius, max 10km
            completedSearchesRef.current = 0; // Reset counter
            processedPlaceIds.clear();

            // Search again with expanded radius
            for (const query of searchQueries) {
              await processSearchResult(query, searchRadiusRef.current);
            }
            return;
          }

          // Deduplicate by placeId before sorting
          const uniqueLeads = [];
          const seenPlaceIds = new Set();
          for (const lead of allFetchedResultsRef.current) {
            if (lead.placeId && !seenPlaceIds.has(lead.placeId)) {
              seenPlaceIds.add(lead.placeId);
              uniqueLeads.push(lead);
            } else if (!lead.placeId) {
              uniqueLeads.push(lead);
            }
          }

          // Final sort by priority
          const sorted = uniqueLeads.sort((a, b) => {
            return (b.priority || 0) - (a.priority || 0);
          });

          // Cache the results
          cacheSearchResults(location, cacheKey, searchRadiusRef.current, sorted);

          setAllLeads(sorted);
          setLoading(false);
          pendingResultsRef.current = [];
        }
      } catch (error) {
        console.error(`Error searching for "${query}":`, error);
        completedSearchesRef.current++;
        
        // If all searches are done (including errors), finalize
        if (completedSearchesRef.current === totalSearches) {
          const uniqueLeads = [];
          const seenPlaceIds = new Set();
          for (const lead of allFetchedResultsRef.current) {
            if (lead.placeId && !seenPlaceIds.has(lead.placeId)) {
              seenPlaceIds.add(lead.placeId);
              uniqueLeads.push(lead);
            }
          }
          const sorted = uniqueLeads.sort((a, b) => {
            return (b.priority || 0) - (a.priority || 0);
          });
          setAllLeads(sorted);
          setLoading(false);
        }
      }
    };

    // Process all search queries
    for (const query of searchQueries) {
      processSearchResult(query);
    }
  };

  // Format lead data - now using formatPlaceDataNew from Places API (New)
  // This function is kept for backward compatibility with cached data
  const formatLeadData = (place) => {
    // If it's already in the new format, use it directly
    if (place.id && (place.nationalPhoneNumber || place.internationalPhoneNumber)) {
      return formatPlaceDataNew(place);
    }
    
    // Legacy format support (for cached data)
    const phone = place.formatted_phone_number || place.international_phone_number || 
                  place.nationalPhoneNumber || null;
    
    // Must have phone number
    if (!phone) {
      return null;
    }
    
    const name = place.name || place.displayName?.text || place.displayName || 'N/A';
    const address = place.formatted_address || place.formattedAddress || null;
    
    return {
      name: name,
      phone: phone,
      email: null,
      website: null,
      address: address,
      coordinates: null,
      lat: null,
      lng: null,
      placeId: place.place_id || place.id || null,
      rating: null,
      userRatingsTotal: 0,
      businessStatus: null,
      types: [],
      contacted: contactedLeads.has(place.place_id || place.id) || false
    };
  };

  // Handle search submission
  const handleSearch = async (e) => {
    e?.preventDefault();
    
    if (!searchQuery.trim()) {
      setError("Please enter a search query");
      return;
    }

    // Check if Google Maps is available
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      setError("Google Maps is still loading. Please wait a moment and try again.");
      return;
    }

    // Initialize services if they're not set
    let serviceToUse = placesService;
    let geocoderToUse = geocoder;
    
    if (!serviceToUse) {
      try {
        serviceToUse = new window.google.maps.places.PlacesService(document.createElement('div'));
        setPlacesService(serviceToUse);
      } catch (error) {
        console.error("Error creating PlacesService:", error);
        setError("Failed to initialize Google Maps. Please refresh the page.");
        return;
      }
    }

    if (!geocoderToUse) {
      try {
        geocoderToUse = new window.google.maps.Geocoder();
        setGeocoder(geocoderToUse);
      } catch (error) {
        console.error("Error creating Geocoder:", error);
      }
    }

    // Clear any pending updates
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
      updateTimeoutRef.current = null;
    }
    pendingResultsRef.current = [];
    
    setLoading(true);
    setError(null);
    setAllLeads([]);
    setDisplayedLeads([]);
    setCurrentPage(1);
    allFetchedResultsRef.current = [];
    searchRadiusRef.current = 2000; // Reset to 2km for new search
    setSelectedLeads(new Set());

    // Try to find the place first
    serviceToUse.textSearch(
      {
        query: searchQuery,
        type: 'establishment',
        componentRestrictions: { country: 'in' }
      },
      async (places, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && places && places.length > 0) {
          const place = places[0];
          await fetchPlaceDetails(place.place_id, place.geometry?.location);
        } else {
          // If no specific place found, try geocoding to get location
          if (geocoderToUse) {
            geocoderToUse.geocode(
              { address: searchQuery, componentRestrictions: { country: 'in' } },
              async (results, geoStatus) => {
                if (geoStatus === window.google.maps.GeocoderStatus.OK && results && results.length > 0) {
                  const location = results[0].geometry.location;
                  const addressComponents = results[0].address_components || [];
                  
                  // Detect if it's an area (has locality/suburb) or just a city
                  const hasLocality = addressComponents.some(ac => 
                    ac.types.includes('locality') || ac.types.includes('sublocality') || 
                    ac.types.includes('sublocality_level_1') || ac.types.includes('neighborhood')
                  );
                  
                  setIsAreaSearch(hasLocality);
                  setSearchLocation(location);
                  
                  await searchNearbyBusinesses(location, hasLocality);
                } else {
                  setError("Could not find the specified location. Please try a different search term.");
                  setLoading(false);
                }
              }
            );
          } else {
            setError("Could not find the specified location. Please try a different search term.");
            setLoading(false);
          }
        }
      }
    );
  };

  // Load shortlisted lead IDs from Firestore - ALL users (to prevent duplicates across users)
  useEffect(() => {
    if (!user) return;
    
    // Load ALL shortlisted leads from ALL users to prevent cross-user duplicates
    const unsubscribe = firestore
      .collection("shortlistedLeads")
      .onSnapshot((snapshot) => {
        const ids = new Set();
        const quotePlaceIds = new Set(); // Track leads that have quotes
        const leadOwners = new Map(); // Track which user owns each lead
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (data.placeId) {
            ids.add(data.placeId);
            // If lead has a quoteId, mark it as having a quote
            if (data.quoteId) {
              quotePlaceIds.add(data.placeId);
            }
            // Store the owner info for error messages
            if (!leadOwners.has(data.placeId)) {
              leadOwners.set(data.placeId, {
                userId: data.userId,
                name: data.name || 'Unknown'
              });
            }
          }
        });
        setShortlistedLeadIds(ids);
        setLeadsWithQuotes(quotePlaceIds);
        // Store lead owners for checking during transfer (use ref to persist across renders)
        if (!window.leadOwnersMap) {
          window.leadOwnersMap = new Map();
        }
        // Update the map with current owners
        leadOwners.forEach((value, key) => {
          window.leadOwnersMap.set(key, value);
        });
      }, (error) => {
        console.error("Error loading shortlisted leads:", error);
      });

    return () => unsubscribe();
  }, [user]);

  // Load contacted leads from Firestore - ALL users (to prevent showing contacted leads to other agents)
  useEffect(() => {
    if (!user) return;
    
    // Load ALL contacted leads from ALL users to prevent cross-user duplicates
    const unsubscribe = firestore
      .collection("contactedLeads")
      .onSnapshot((snapshot) => {
        const ids = new Set();
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (data.placeId) {
            ids.add(data.placeId);
          }
        });
        setContactedLeads(ids);
      }, (error) => {
        console.error("Error loading contacted leads:", error);
      });

    return () => unsubscribe();
  }, [user]);

  // Load disqualified leads from Firestore - ALL users (to prevent showing disqualified leads to other agents)
  useEffect(() => {
    if (!user) return;
    
    // Load ALL disqualified leads from ALL users to prevent cross-user duplicates
    const unsubscribe = firestore
      .collection("disqualifiedLeads")
      .onSnapshot((snapshot) => {
        const ids = new Set();
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (data.placeId) {
            ids.add(data.placeId);
          }
        });
        setDisqualifiedLeadIds(ids);
      }, (error) => {
        console.error("Error loading disqualified leads:", error);
      });

    return () => unsubscribe();
  }, [user]);

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    if (searchQuery && allLeads.length > 0) {
      try {
        const stateToSave = {
          query: searchQuery,
          allLeads: allLeads.map(lead => ({
            ...lead,
            // Convert LatLng objects to plain objects for storage
            lat: typeof lead.lat === 'function' ? lead.lat() : lead.lat,
            lng: typeof lead.lng === 'function' ? lead.lng() : lead.lng
          })),
          currentPage: currentPage,
          scrollPosition: scrollPositionRef.current,
          isAreaSearch: isAreaSearch,
          searchLocation: searchLocation ? {
            lat: typeof searchLocation.lat === 'function' ? searchLocation.lat() : searchLocation.lat,
            lng: typeof searchLocation.lng === 'function' ? searchLocation.lng() : searchLocation.lng
          } : null,
          searchRadius: searchRadiusRef.current
        };
        sessionStorage.setItem('leadsSearchState', JSON.stringify(stateToSave));
      } catch (error) {
        console.error("Error saving state:", error);
      }
    }
  }, [searchQuery, allLeads, currentPage, isAreaSearch, searchLocation]);

  // Also save on component unmount (when navigating away)
  useEffect(() => {
    return () => {
      if (searchQuery && allLeads.length > 0) {
        try {
          const stateToSave = {
            query: searchQuery,
            allLeads: allLeads.map(lead => ({
              ...lead,
              lat: typeof lead.lat === 'function' ? lead.lat() : lead.lat,
              lng: typeof lead.lng === 'function' ? lead.lng() : lead.lng
            })),
            currentPage: currentPage,
            scrollPosition: scrollPositionRef.current,
            isAreaSearch: isAreaSearch,
            searchLocation: searchLocation ? {
              lat: typeof searchLocation.lat === 'function' ? searchLocation.lat() : searchLocation.lat,
              lng: typeof searchLocation.lng === 'function' ? searchLocation.lng() : searchLocation.lng
            } : null,
            searchRadius: searchRadiusRef.current
          };
          sessionStorage.setItem('leadsSearchState', JSON.stringify(stateToSave));
        } catch (error) {
          console.error("Error saving state on unmount:", error);
        }
      }
    };
  }, [searchQuery, allLeads, currentPage, isAreaSearch, searchLocation]);

  // Restore state from sessionStorage on mount
  useEffect(() => {
    const savedState = sessionStorage.getItem('leadsSearchState');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        if (state.query && state.allLeads && state.allLeads.length > 0) {
          setSearchQuery(state.query);
          // Deduplicate leads when restoring from session storage
          const uniqueLeads = [];
          const seenPlaceIds = new Set();
          if (state.allLeads && Array.isArray(state.allLeads)) {
            for (const lead of state.allLeads) {
              if (lead.placeId && !seenPlaceIds.has(lead.placeId)) {
                seenPlaceIds.add(lead.placeId);
                uniqueLeads.push(lead);
              } else if (!lead.placeId) {
                uniqueLeads.push(lead);
              }
            }
          }
          setAllLeads(uniqueLeads);
          setCurrentPage(state.currentPage || 1);
          scrollPositionRef.current = state.scrollPosition || 0;
          
          // Restore search parameters
          if (state.isAreaSearch !== undefined) {
            setIsAreaSearch(state.isAreaSearch);
          }
          if (state.searchLocation && window.google && window.google.maps) {
            const location = new window.google.maps.LatLng(
              state.searchLocation.lat,
              state.searchLocation.lng
            );
            setSearchLocation(location);
          }
          if (state.searchRadius) {
            searchRadiusRef.current = state.searchRadius;
          }
          
          // Restore scroll position after a brief delay
          setTimeout(() => {
            if (scrollPositionRef.current > 0) {
              window.scrollTo(0, scrollPositionRef.current);
            }
          }, 200);
        }
      } catch (error) {
        console.error("Error restoring state:", error);
      }
    }
  }, []);

  // Save scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        scrollPositionRef.current = containerRef.current.scrollTop;
      } else {
        // Fallback to window scroll
        scrollPositionRef.current = window.scrollY;
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    } else {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [displayedLeads.length]);

  // Filter definitions - More precise and mutually exclusive
  const filterOptions = {
    default: {
      label: "All Leads (Default)",
      filter: (lead) => true // No filtering, show all
    },
    warehouses: {
      label: "Warehouses & Storage",
      // useAI: true, // AI filtering disabled
      filter: (lead) => {
        const types = (lead.types || []).map(t => t.toLowerCase());
        const name = (lead.name || '').toLowerCase();
        const address = (lead.address || '').toLowerCase();
        // Specific terms for warehouses/storage - be more inclusive
        const warehouseTerms = ['warehouse', 'godown', 'cold storage', 'cold_storage', 'depot', 'storehouse', 'storage', 'logistics', 'distribution'];
        // Only exclude if it's clearly NOT a warehouse (be less strict)
        const excludeTerms = ['restaurant', 'hotel', 'mall', 'hospital', 'school', 'university'];
        const hasExcludeTerm = excludeTerms.some(term => 
          (name.includes(term) || address.includes(term) || types.some(type => type.includes(term))) &&
          !warehouseTerms.some(wt => name.includes(wt) || address.includes(wt) || types.some(type => type.includes(wt)))
        );
        if (hasExcludeTerm) return false;
        // Check for warehouse-specific priority (9 for storage)
        const isStoragePriority = lead.priority === 9;
        // Be more inclusive - include if it has warehouse terms OR if types suggest storage
        return isStoragePriority ||
               warehouseTerms.some(term => name.includes(term)) ||
               warehouseTerms.some(term => address.includes(term)) ||
               types.some(type => warehouseTerms.some(term => type.includes(term))) ||
               types.some(type => type.includes('storage') || type.includes('warehouse') || type.includes('logistics'));
      }
    },
    offices: {
      label: "Office Spaces",
      filter: (lead) => {
        const name = (lead.name || '').toLowerCase();
        // Specific terms for offices
        const officeTerms = ['office', 'corporate', 'business park', 'coworking', 'commercial office', 'business center', 'workspace'];
        // Only exclude if it's clearly NOT an office
        const excludeTerms = ['restaurant', 'hotel', 'mall', 'hospital', 'school', 'warehouse', 'factory'];
        const hasExcludeTerm = excludeTerms.some(term => 
          name.includes(term) && !officeTerms.some(ot => name.includes(ot))
        );
        if (hasExcludeTerm) return false;
        // Check for office-specific priority (7)
        const isOfficePriority = lead.priority === 7;
        // Include if it has office terms
        return isOfficePriority || officeTerms.some(term => name.includes(term));
      }
    },
    manufacturing: {
      label: "Manufacturing & Industrial",
      filter: (lead) => {
        // Basic pre-filtering - exclude obvious non-matches
        const types = (lead.types || []).map(t => t.toLowerCase());
        const name = (lead.name || '').toLowerCase();
        const address = (lead.address || '').toLowerCase();
        
        // Exclude warehouse/storage terms
        const excludeTerms = ['warehouse', 'godown', 'storage', 'depot', 'storehouse'];
        const hasExcludeTerm = excludeTerms.some(term => 
          name.includes(term) || address.includes(term) || types.some(type => type.includes(term))
        );
        if (hasExcludeTerm) return false;
        
        // Exclude obvious brand name patterns
        const brandNamePatterns = ['meat factory', 'fashion factory', 'food factory', 'clothing factory', 
                                   'textile factory', 'bakery factory', 'restaurant factory', 'cafe factory',
                                   'store factory', 'shop factory', 'outlet factory', 'showroom factory'];
        const isBrandName = brandNamePatterns.some(pattern => name.includes(pattern));
        if (isBrandName) return false;
        
        // If it has "factory" in name but only generic types, exclude it (will be verified by AI if it passes basic filter)
        const hasOnlyGenericTypes = types.length > 0 && types.every(type => 
          type === 'establishment' || 
          type === 'point_of_interest' || 
          type === 'store' ||
          type === 'clothing_store' ||
          type === 'restaurant' ||
          type === 'food' ||
          type === 'cafe'
        );
        
        if (name.includes('factory') && hasOnlyGenericTypes) {
          // Let AI decide - return true for now, AI will filter it out if it's a brand
          return true;
        }
        
        // Check for manufacturing types
        const hasManufacturingTypes = types.some(type => 
          type.includes('factory') || 
          type.includes('manufacturing') || 
          type.includes('industrial') ||
          type.includes('plant') ||
          type.includes('workshop')
        );
        
        // Specific terms for manufacturing/industrial
        const manufacturingTerms = ['manufacturing', 'industrial', 'plant', 'mill', 'works', 'workshop', 'production', 'foundry'];
        const hasFactoryInName = name.includes('factory') && (hasManufacturingTypes || address.includes('factory'));
        const isManufacturingPriority = lead.priority === 10 && 
          !name.includes('warehouse') && !name.includes('storage') && !name.includes('godown') &&
          hasManufacturingTypes;
        
        return isManufacturingPriority ||
               hasFactoryInName ||
               manufacturingTerms.some(term => name.includes(term)) ||
               manufacturingTerms.some(term => address.includes(term)) ||
               hasManufacturingTypes;
      }
      // useAI: true // AI filtering disabled
    },
    retail: {
      label: "Retail & Shopping",
      // useAI: true, // AI filtering disabled
      filter: (lead) => {
        const types = (lead.types || []).map(t => t.toLowerCase());
        const name = (lead.name || '').toLowerCase();
        const address = (lead.address || '').toLowerCase();
        // Specific terms for retail/shopping - be more inclusive
        const retailTerms = ['mall', 'shopping', 'supermarket', 'hypermarket', 'bazaar', 'retail', 'store', 'market', 'shopping center', 'shopping mall', 'department store'];
        // Only exclude if it's clearly NOT retail (be less strict)
        const excludeTerms = ['warehouse', 'factory', 'manufacturing', 'hotel', 'hospital', 'school'];
        const hasExcludeTerm = excludeTerms.some(term => 
          (name.includes(term) || address.includes(term) || types.some(type => type.includes(term))) &&
          !retailTerms.some(rt => name.includes(rt) || address.includes(rt) || types.some(type => type.includes(rt)))
        );
        if (hasExcludeTerm) return false;
        // Check for retail-specific priority (8)
        const isRetailPriority = lead.priority === 8;
        // Be more inclusive - include if it has retail terms OR if types suggest retail
        return isRetailPriority ||
               retailTerms.some(term => name.includes(term)) ||
               retailTerms.some(term => address.includes(term)) ||
               types.some(type => type.includes('store') || type.includes('shopping') || type.includes('mall') || type.includes('market') || type.includes('retail'));
      }
    },
    hospitality: {
      label: "Hotels & Hospitality",
      filter: (lead) => {
        const name = (lead.name || '').toLowerCase();
        // Specific terms for hospitality only
        const hospitalityTerms = ['hotel', 'resort', 'lodge', 'inn', 'hospitality'];
        // Exclude other categories
        const excludeTerms = ['warehouse', 'factory', 'office', 'mall', 'hospital', 'school'];
        const hasExcludeTerm = excludeTerms.some(term => 
          name.includes(term) && !hospitalityTerms.some(ht => name.includes(ht))
        );
        if (hasExcludeTerm) return false;
        // Check for hospitality-specific priority (8)
        const isHospitalityPriority = lead.priority === 8 && 
          (name.includes('hotel') || name.includes('resort'));
        return isHospitalityPriority || hospitalityTerms.some(term => name.includes(term));
      }
    },
    healthcare: {
      label: "Healthcare & Hospitals",
      filter: (lead) => {
        const name = (lead.name || '').toLowerCase();
        // Specific terms for healthcare only
        const healthcareTerms = ['hospital', 'clinic', 'medical', 'healthcare', 'diagnostic', 'pharmacy', 'health center'];
        // Exclude other categories
        const excludeTerms = ['warehouse', 'factory', 'office', 'mall', 'hotel', 'school'];
        const hasExcludeTerm = excludeTerms.some(term => 
          name.includes(term) && !healthcareTerms.some(ht => name.includes(ht))
        );
        if (hasExcludeTerm) return false;
        // Check for healthcare-specific priority (7)
        const isHealthcarePriority = lead.priority === 7 && 
          (name.includes('hospital') || name.includes('clinic'));
        return isHealthcarePriority || healthcareTerms.some(term => name.includes(term));
      }
    },
    education: {
      label: "Schools & Educational",
      filter: (lead) => {
        const name = (lead.name || '').toLowerCase();
        // Specific terms for education only
        const educationTerms = ['school', 'university', 'college', 'institute', 'academy', 'learning center'];
        // Exclude other categories
        const excludeTerms = ['warehouse', 'factory', 'office', 'mall', 'hotel', 'hospital'];
        const hasExcludeTerm = excludeTerms.some(term => 
          name.includes(term) && !educationTerms.some(et => name.includes(et))
        );
        if (hasExcludeTerm) return false;
        // Check for education-specific priority (7)
        const isEducationPriority = lead.priority === 7 && 
          (name.includes('school') || name.includes('university') || name.includes('college'));
        return isEducationPriority || educationTerms.some(term => name.includes(term));
      }
    },
    fuelStations: {
      label: "Fuel Stations & Petrol Pumps",
      filter: (lead) => {
        // Use the smart fuel station detection algorithm
        return isFuelStation(lead);
      }
    }
  };

  // Reset to page 1 when filter type changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter]);

  // Smart algorithm to detect fuel stations
  const isFuelStation = (lead) => {
    const name = (lead.name || '').toLowerCase();
    
    // Note: We don't have types anymore, so we rely on name matching
    
    // Major fuel brands in India (comprehensive list - case-insensitive matching)
    const fuelBrands = [
      // Public Sector - Major 3
      'indian oil', 'ioc', 'iocl', 'indianoil', 'indian oil corporation',
      'hpcl', 'hindustan petroleum', 'hp petroleum', 'hindustan petroleum corporation',
      'bharat petroleum', 'bpcl', 'bp petroleum', 'bharat petroleum corporation',
      // Private Sector - Major brands
      'reliance petrol', 'reliance fuel', 'rjil', 'reliance jio', 'reliance industries',
      'essar', 'essar petrol', 'essar oil',
      'shell', 'shell india', 'shell stations',
      'nayara', 'nayara energy', 'nayara petrol',
      'jio-bp', 'jiobp', 'jio bp', 'reliance bp',
      // Other popular brands
      'tide water', 'tidewater', 'tide water oil',
      'gulf oil', 'gulf', 'gulf petroleum',
      'castrol', 'castrol india',
      'total', 'total india', 'total petrol',
      'rosneft', 'rosneft india',
      'adani total', 'adani total gas', 'adani wilmar',
      'ongc', 'oil and natural gas corporation',
      'petronet', 'petronet lng',
      'oil india', 'oil india limited',
      // Regional and other brands
      'vedanta cairn',
      'mrpl', 'mangalore refinery',
      'chennai petroleum', 'cpcl',
      'numaligarh refinery', 'nrpl',
      'bongaigaon refinery', 'brpl',
      'bharat oman refinery', 'borl'
    ];
    
    // Check if name contains any fuel brand
    const hasFuelBrand = fuelBrands.some(brand => name.includes(brand));
    if (hasFuelBrand) return true;
    
    // Common fuel station keywords (multiple languages)
    const fuelKeywords = [
      // English
      'petrol pump', 'petrolpump', 'petrol bunk', 'petrolbunk',
      'gas station', 'gasstation', 'gas pump', 'gaspump',
      'fuel station', 'fuelstation', 'fuel pump', 'fuelpump',
      'filling station', 'fillingstation',
      'petrol station', 'petrolstation',
      'diesel pump', 'dieselpump',
      'cng station', 'cngstation', 'cng pump', 'cngpump',
      'lpg station', 'lpgstation',
      // Hindi/Indian terms
      'petrol pump', 'पेट्रोल पंप',
      'gas station', 'गैस स्टेशन',
      'fuel bunk', 'fuelbunk',
      // Variations
      'pump', // But only if combined with fuel-related terms
      'bunk',
      'filling'
    ];
    
    // Check name for fuel keywords (with context)
    const nameHasFuelKeyword = fuelKeywords.some(keyword => {
      if (keyword === 'pump' || keyword === 'bunk' || keyword === 'filling') {
        // Only match if there's additional fuel context
        return (name.includes('petrol') || name.includes('diesel') || 
                name.includes('gas') || name.includes('fuel') || 
                name.includes('cng') || name.includes('lpg')) &&
               (name.includes(keyword));
      }
      return name.includes(keyword);
    });
    
    if (nameHasFuelKeyword) return true;
    
    // Note: We don't have address anymore, so we skip address-based matching
    
    // Check for specific patterns in name (e.g., "XYZ Petrol Pump", "ABC Gas Station")
    const fuelPatterns = [
      /\b(petrol|diesel|gas|fuel|cng|lpg)\s*(pump|station|bunk|bunk|outlet)\b/i,
      /\b(pump|station|bunk)\s*(petrol|diesel|gas|fuel|cng|lpg)\b/i,
      /\b(ioc|hpcl|bpcl|shell|reliance|essar|nayara)\s*(petrol|pump|station|outlet)\b/i
    ];
    
    const matchesPattern = fuelPatterns.some(pattern => pattern.test(name));
    if (matchesPattern) return true;
    
    // False positives to avoid - businesses that might have "pump" in name but aren't fuel stations
    const falsePositives = [
      'water pump', 'waterpump', 'pump house', 'pumphouse',
      'pump station', 'pumping station', 'pump room',
      'fire pump', 'firepump', 'booster pump',
      'sump pump', 'sump', 'drain pump'
    ];
    
    const isFalsePositive = falsePositives.some(fp => name.includes(fp));
    
    if (isFalsePositive) return false;
    
    // If name contains both a fuel-related term AND a business type, it's likely a fuel station
    const hasFuelTerm = name.includes('petrol') || name.includes('diesel') || 
                        name.includes('gas') || name.includes('fuel') ||
                        name.includes('cng') || name.includes('lpg');
    const hasBusinessTerm = name.includes('pump') || name.includes('station') || 
                           name.includes('bunk') || name.includes('outlet');
    
    if (hasFuelTerm && hasBusinessTerm) return true;
    
    return false;
  };

  // Smart filtering algorithm with scoring and sorting
  const calculateMatchScore = (lead, filterType) => {
    const name = (lead.name || '').toLowerCase();
    let score = 0;
    
    // Base scoring based on filter type
    const scoringRules = {
      warehouses: {
        primaryTerms: ['warehouse', 'godown', 'cold storage', 'cold_storage', 'depot', 'storehouse', 'storage', 'logistics', 'distribution'],
        secondaryTerms: ['storage', 'warehouse', 'logistics'],
        typeMatches: ['storage', 'warehouse', 'logistics', 'distribution'],
        excludeTerms: ['restaurant', 'hotel', 'mall', 'hospital', 'school', 'university'],
        priority: 9
      },
      offices: {
        primaryTerms: ['office', 'corporate', 'business park', 'coworking', 'commercial office', 'business center', 'workspace'],
        secondaryTerms: ['office', 'business', 'corporate'],
        typeMatches: ['office', 'business', 'corporate'],
        excludeTerms: ['restaurant', 'hotel', 'mall', 'hospital', 'school', 'warehouse', 'factory'],
        priority: 7
      },
      manufacturing: {
        primaryTerms: ['manufacturing', 'factory', 'industrial', 'plant', 'mill', 'works', 'workshop', 'production', 'foundry'],
        secondaryTerms: ['factory', 'manufacturing', 'industrial', 'plant'],
        typeMatches: ['factory', 'manufacturing', 'industrial', 'plant', 'workshop'],
        excludeTerms: ['warehouse', 'godown', 'storage', 'depot', 'storehouse', 'restaurant', 'cafe', 'store', 'shop'],
        priority: 10
      },
      retail: {
        primaryTerms: ['mall', 'shopping', 'supermarket', 'hypermarket', 'bazaar', 'retail', 'store', 'market', 'shopping center', 'shopping mall', 'department store'],
        secondaryTerms: ['mall', 'shopping', 'store', 'market', 'retail'],
        typeMatches: ['store', 'shopping', 'mall', 'market', 'retail'],
        excludeTerms: ['warehouse', 'factory', 'manufacturing', 'hotel', 'hospital', 'school'],
        priority: 8
      },
      hospitality: {
        primaryTerms: ['hotel', 'resort', 'lodge', 'inn', 'hospitality'],
        secondaryTerms: ['hotel', 'resort', 'lodge', 'inn'],
        typeMatches: ['hotel', 'resort', 'lodge', 'inn'],
        excludeTerms: ['warehouse', 'factory', 'office', 'mall', 'hospital', 'school'],
        priority: 8
      },
      healthcare: {
        primaryTerms: ['hospital', 'clinic', 'medical', 'healthcare', 'diagnostic', 'pharmacy', 'health center'],
        secondaryTerms: ['hospital', 'clinic', 'medical', 'healthcare'],
        typeMatches: ['hospital', 'clinic', 'medical', 'healthcare'],
        excludeTerms: ['warehouse', 'factory', 'office', 'mall', 'hotel', 'school'],
        priority: 7
      },
      education: {
        primaryTerms: ['school', 'university', 'college', 'institute', 'academy', 'learning center'],
        secondaryTerms: ['school', 'university', 'college', 'institute'],
        typeMatches: ['school', 'university', 'college', 'institute'],
        excludeTerms: ['warehouse', 'factory', 'office', 'mall', 'hotel', 'hospital'],
        priority: 7
      },
      fuelStations: {
        primaryTerms: ['petrol pump', 'gas station', 'fuel station', 'petrol bunk', 'petrolpump', 'gasstation', 'fuelpump'],
        secondaryTerms: ['petrol', 'gas', 'fuel', 'diesel', 'cng', 'lpg'],
        typeMatches: ['gas_station', 'gas_station_with_convenience_store', 'fuel', 'petrol_pump'],
        excludeTerms: ['warehouse', 'factory', 'office', 'mall', 'hotel', 'hospital', 'water pump', 'fire pump'],
        priority: 0 // Fuel stations are not priority for solar, but we want to find them
      }
    };
    
    const rules = scoringRules[filterType];
    if (!rules) return 0;
    
    // Check for exclusion terms - if found, return 0
    const hasExcludeTerm = rules.excludeTerms.some(term => 
      name.includes(term) && !rules.primaryTerms.some(pt => name.includes(pt))
    );
    if (hasExcludeTerm) return 0;
    
    // Score based on priority match
    if (lead.priority === rules.priority) {
      score += 100;
    }
    
    // Score based on primary terms in name (highest weight)
    rules.primaryTerms.forEach(term => {
      if (name.includes(term)) score += 50;
    });
    
    // Score based on secondary terms
    rules.secondaryTerms.forEach(term => {
      if (name.includes(term)) score += 20;
    });
    
    // Bonus for having phone
    if (lead.phone && lead.phone !== 'Not available') {
      score += 10;
    }
    
    return score;
  };

  // Update displayed leads when page, leads, or filter change
  useEffect(() => {
    const applyFilters = () => {
      const startIndex = (currentPage - 1) * 50;
      const endIndex = startIndex + 50;
      
      // Apply filters - exclude:
      // 1. Leads that are shortlisted by any agent (in any stage)
      // 2. Leads that are contacted by any agent
      // 3. Leads that have quotes generated by any agent
      // 4. Leads that are disqualified by any agent
      let filtered = allLeads.filter(lead => {
        const placeId = lead.placeId;
        if (!placeId) return true; // Include leads without placeId (shouldn't happen, but handle it)
        
        // Exclude if shortlisted by any agent
        if (shortlistedLeadIds.has(placeId)) return false;
        
        // Exclude if contacted by any agent
        if (contactedLeads.has(placeId)) return false;
        
        // Exclude if has quote generated by any agent
        if (leadsWithQuotes.has(placeId)) return false;
        
        // Exclude if disqualified by any agent
        if (disqualifiedLeadIds.has(placeId)) return false;
        
        return true;
      });
      
      if (activeFilter !== 'default' && filterOptions[activeFilter]) {
        const filterOption = filterOptions[activeFilter];
        
        // Apply basic filter first
        const filterFunc = filterOption.filter;
        filtered = filtered.filter(lead => {
          try {
            return filterFunc(lead);
          } catch (error) {
            console.error('Filter error:', error, lead);
            return false;
          }
        });
        
        // Apply smart scoring and sorting
        filtered = filtered.map(lead => ({
          ...lead,
          matchScore: calculateMatchScore(lead, activeFilter)
        }))
        .filter(lead => lead.matchScore > 0) // Only include leads with positive scores
        .sort((a, b) => {
          // Sort by match score (descending), then by priority (descending), then by rating (descending)
          if (b.matchScore !== a.matchScore) {
            return b.matchScore - a.matchScore;
          }
          if (b.priority !== a.priority) {
            return (b.priority || 0) - (a.priority || 0);
          }
          return (b.rating || 0) - (a.rating || 0);
        });
      }
      
      // Reset to page 1 if current page is beyond available pages
      const newTotalPages = Math.ceil(filtered.length / 50);
      if (currentPage > newTotalPages && filtered.length > 0) {
        setCurrentPage(1);
        return; // Will re-run with page 1
      }
      
      setDisplayedLeads(filtered.slice(startIndex, endIndex));
      setHasMorePages(endIndex < filtered.length);
      setFilteredLeadsCount(filtered.length); // Update filtered count
    };
    
    applyFilters();
  }, [currentPage, allLeads, shortlistedLeadIds, contactedLeads, leadsWithQuotes, disqualifiedLeadIds, activeFilter]);

  // Calculate total pages with filtered leads
  const totalPages = Math.ceil(filteredLeadsCount / 50) || 1;
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll to top of results
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // Handle lead selection
  const toggleLeadSelection = (placeId) => {
    // Check if this lead is already saved by another user
    if (shortlistedLeadIds.has(placeId)) {
      const leadOwners = window.leadOwnersMap || new Map();
      const owner = leadOwners.get(placeId);
      
      if (owner && owner.userId !== user?.uid) {
        showNotification(
          `This lead is already in the system for another user and cannot be selected.`,
          "error"
        );
        return; // Don't allow selection
      }
    }
    
    setSelectedLeads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(placeId)) {
        newSet.delete(placeId);
      } else {
        newSet.add(placeId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedLeads.size === displayedLeads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(displayedLeads.map(l => l.placeId).filter(Boolean)));
    }
  };

  // Transfer selected leads to shortlisted
  const transferToShortlisted = async () => {
    if (selectedLeads.size === 0) {
      showNotification("Please select at least one lead to transfer", "error");
      return;
    }

    if (!user) {
      showNotification("You must be logged in to save leads", "error");
      return;
    }

    try {
      // Check if any selected leads are already saved by another user
      const selectedLeadData = allLeads.filter(l => selectedLeads.has(l.placeId));
      const alreadySavedLeads = [];
      const newLeads = [];
      
      // Check each lead against all shortlisted leads
      for (const lead of selectedLeadData) {
        if (shortlistedLeadIds.has(lead.placeId)) {
          // Check if it's saved by current user or another user
          const leadOwners = window.leadOwnersMap || new Map();
          const owner = leadOwners.get(lead.placeId);
          
          if (owner && owner.userId !== user.uid) {
            // This lead is already saved by another user
            alreadySavedLeads.push(lead.name || 'Unknown');
          } else {
            // It's already saved by current user, skip it
            console.log(`Lead "${lead.name}" is already saved by you`);
          }
        } else {
          // New lead, can be saved
          newLeads.push(lead);
        }
      }

      // Show error if any leads are already saved by another user
      if (alreadySavedLeads.length > 0) {
        const leadNames = alreadySavedLeads.slice(0, 3).join(', ');
        const moreText = alreadySavedLeads.length > 3 ? ` and ${alreadySavedLeads.length - 3} more` : '';
        showNotification(
          `Cannot save: ${leadNames}${moreText} ${alreadySavedLeads.length === 1 ? 'is' : 'are'} already in the system for another user.`,
          "error"
        );
        
        // Remove already-saved leads from selection
        const newSelected = new Set(selectedLeads);
        selectedLeadData.forEach(lead => {
          if (alreadySavedLeads.includes(lead.name)) {
            newSelected.delete(lead.placeId);
          }
        });
        setSelectedLeads(newSelected);
        
        // If no new leads to save, return early
        if (newLeads.length === 0) {
          return;
        }
      }

      // Save only the new leads
      if (newLeads.length === 0) {
        return;
      }

      const batch = firestore.batch();

      newLeads.forEach(lead => {
        const docRef = firestore.collection("shortlistedLeads").doc();
        batch.set(docRef, {
          userId: user.uid,
          placeId: lead.placeId,
          name: lead.name,
          phone: lead.phone,
          address: lead.address || null,
          website: lead.website || null,
          coordinates: lead.coordinates || null,
          rating: lead.rating || null,
          userRatingsTotal: lead.userRatingsTotal || 0,
          businessStatus: lead.businessStatus || null,
          types: lead.types || [],
          priority: lead.priority || 0,
          stage: 'shown_interest', // Initial stage
          transferredAt: Date.now(),
          notes: "", // Initialize notes field as empty string
          notesUpdatedAt: null
        });
      });

      await batch.commit();
      const count = newLeads.length;
      setSelectedLeads(new Set());
      
      if (alreadySavedLeads.length > 0) {
        showNotification(
          `Successfully transferred ${count} lead(s). ${alreadySavedLeads.length} lead(s) were skipped as they're already saved by another user.`,
          "warning"
        );
      } else {
        showNotification(`Successfully transferred ${count} lead(s) to shortlisted`, "success");
      }
    } catch (error) {
      console.error("Error transferring leads:", error);
      showNotification("Failed to transfer leads. Please try again.", "error");
    }
  };

  // Mark lead as contacted
  const markAsContacted = async (placeId) => {
    if (!user) return;

    try {
      const contactedDate = new Date();
      await firestore.collection("contactedLeads").add({
        userId: user.uid,
        placeId: placeId,
        contactedAt: Date.now(),
        contactedDate: contactedDate.toISOString().split('T')[0], // YYYY-MM-DD format
        contactedTimestamp: contactedDate.getTime()
      });
    } catch (error) {
      console.error("Error marking lead as contacted:", error);
    }
  };

  // Mark lead as disqualified
  const markAsDisqualified = async (lead) => {
    if (!user) {
      showNotification("You must be logged in to disqualify leads", "error");
      return;
    }

    if (!lead || !lead.placeId) {
      showNotification("Invalid lead data. Cannot disqualify.", "error");
      return;
    }

    try {
      // Save to disqualifiedLeads collection FIRST - don't remove until write succeeds
      const leadData = {
        userId: user.uid,
        placeId: lead.placeId,
        name: lead.name,
        phone: lead.phone,
        address: lead.address || null,
        website: lead.website || null,
        coordinates: lead.coordinates || null,
        rating: lead.rating || null,
        userRatingsTotal: lead.userRatingsTotal || 0,
        businessStatus: lead.businessStatus || null,
        types: lead.types || [],
        priority: lead.priority || 0,
        disqualifiedAt: Date.now(),
        stage: 'disqualified'
      };
      
      const docRef = await firestore.collection("disqualifiedLeads").add(leadData);
      
      // Only remove from UI after successful Firestore write
      setAllLeads(prevLeads => prevLeads.filter(l => l.placeId !== lead.placeId));
      setDisplayedLeads(prevLeads => prevLeads.filter(l => l.placeId !== lead.placeId));
      setDisqualifiedLeadIds(prev => new Set(prev).add(lead.placeId));
      
      showNotification("Lead disqualified successfully", "success");
    } catch (error) {
      console.error("Error disqualifying lead:", error);
      showNotification(`Failed to disqualify lead: ${error.message || 'Please check console for details'}`, "error");
    }
  };

  // Load next page of results
  const loadNextPage = async () => {
    if (loading) return;

    const filtered = allLeads.filter(lead => !shortlistedLeadIds.has(lead.placeId));
    const nextPage = currentPage + 1;
    const startIndex = (nextPage - 1) * 50;
    const endIndex = startIndex + 50;

    // Check if we have more results in current set
    if (endIndex <= filtered.length) {
      // We have enough results, just show next page
      setCurrentPage(nextPage);
    } else {
      // No more results in current set
      setHasMorePages(false);
    }
  };

  // Generate more leads by expanding search
  const generateMoreLeads = async () => {
    if (!searchLocation || loading) return;

    setLoading(true);
    setError(null);
    try {
      // Expand search radius significantly
      const currentRadius = searchRadiusRef.current;
      searchRadiusRef.current = Math.min(currentRadius * 2, 15000); // Max 15km
      
      // Clear all fetched results to allow fetching more with expanded radius
      allFetchedResultsRef.current = [];
      
      // Continue searching with expanded radius (don't use cache for expanded searches)
      await searchNearbyBusinesses(searchLocation, isAreaSearch);
      setHasMorePages(true); // Re-enable pagination after new results
    } catch (error) {
      console.error("Error generating more leads:", error);
      setError("Failed to generate more leads. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Download CSV
  const downloadCSV = () => {
    if (displayedLeads.length === 0) {
      showAlert("No leads to download", "info", "No Leads");
      return;
    }

    const headers = ['Name', 'Phone', 'Address'];
    const rows = displayedLeads.map(lead => [
      lead.name,
      lead.phone || 'Not available',
      lead.address || 'Not available'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `solar_leads_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Check if device is mobile
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  return (
    <div className="fade-in-up">
      <Modal {...modal} onClose={hideModal} />
      {/* Custom Notification */}
      {notification && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: notification.type === 'success' 
              ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" 
              : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
            color: "white",
            padding: "1rem 1.5rem",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.75rem",
            animation: "notificationFadeIn 0.3s ease-out",
            maxWidth: "400px",
            minWidth: "300px"
          }}
        >
          <div style={{ fontSize: "1.25rem", flexShrink: 0 }}>
            {notification.type === 'success' ? '✓' : '✕'}
          </div>
          <div style={{ fontWeight: "500" }}>{notification.message}</div>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: "1rem" }}>
        <h2 className="elegant-section-title" style={{ margin: 0 }}>Find Leads on Maps</h2>
        <button
          onClick={() => history.push("/q/leads/shortlisted")}
          className="btn-elegant btn-elegant-secondary"
          style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
        >
          <FiList size={18} />
          Shortlisted Leads
        </button>
      </div>
      <p style={{ color: "var(--neutral-600)", marginBottom: "1rem", fontSize: "0.9375rem" }}>
        Search for businesses by location, area, pincode, city, or business name to find potential solar rooftop system leads
      </p>
      <div
        style={{
          padding: "0.75rem 1rem",
          background: "rgba(6, 182, 212, 0.1)",
          border: "1px solid rgba(6, 182, 212, 0.3)",
          borderRadius: "8px",
          marginBottom: "2rem",
          fontSize: "0.875rem",
          color: "var(--neutral-700)"
        }}
      >
        <strong>💡 Tip:</strong> Search for warehouses, factories, malls, hotels, offices, or commercial buildings for the best solar rooftop system leads. Results are optimized to show high-priority commercial establishments first.
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} style={{ marginBottom: "2rem" }}>
        <div className="form-group-elegant" style={{ position: "relative", zIndex: 10 }}>
          <label className="form-label-elegant">Search Location or Business</label>
          <div style={{ position: "relative" }}>
            <input
              ref={autocompleteInputRef}
              className="form-input-elegant"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => {
                if (autocompleteSuggestions.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              onBlur={() => {
                // Delay hiding suggestions to allow click on suggestion
                setTimeout(() => setShowSuggestions(false), 200);
              }}
              placeholder="Enter state, area, pincode, city, or business name..."
              style={{ paddingRight: "3rem" }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setAllLeads([]);
                  setDisplayedLeads([]);
                  setCurrentPage(1);
                  setAutocompleteSuggestions([]);
                  setShowSuggestions(false);
                }}
                style={{
                  position: "absolute",
                  right: "0.75rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--neutral-500)",
                  padding: "0.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <FiX size={20} />
              </button>
            )}
            
            {/* Autocomplete Suggestions Dropdown */}
            {showSuggestions && autocompleteSuggestions.length > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  background: "white",
                  border: "2px solid var(--accent-purple)",
                  borderRadius: "8px",
                  marginTop: "0.25rem",
                  maxHeight: "300px",
                  overflowY: "auto",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                  zIndex: 1000
                }}
              >
                {autocompleteSuggestions.map((suggestion, index) => (
                  <div
                    key={suggestion.placeId || index}
                    onClick={() => handleSuggestionSelect(suggestion)}
                    onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                    style={{
                      padding: "0.75rem 1rem",
                      cursor: "pointer",
                      borderBottom: index < autocompleteSuggestions.length - 1 ? "1px solid var(--neutral-200)" : "none",
                      transition: "background 0.2s",
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.25rem"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(139, 92, 246, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "white";
                    }}
                  >
                    <div style={{ 
                      fontWeight: "600", 
                      color: "var(--primary-deep)",
                      fontSize: "0.9375rem"
                    }}>
                      {suggestion.text}
                    </div>
                    {suggestion.description && (
                      <div style={{ 
                        fontSize: "0.875rem", 
                        color: "var(--neutral-600)" 
                      }}>
                        {suggestion.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          type="submit"
          className="btn-elegant btn-elegant-accent"
          style={{ width: "100%", marginTop: "1rem" }}
          disabled={loading || !searchQuery.trim()}
        >
          {loading ? (
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
              <span className="loading-elegant"></span>
              Searching...
            </span>
          ) : (
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
              <FiSearch size={18} />
              Search Leads
            </span>
          )}
        </button>
      </form>

      {/* Error Message */}
      {error && (
        <div
          style={{
            padding: "1rem",
            background: "rgba(239, 68, 68, 0.1)",
            border: "1px solid var(--error)",
            borderRadius: "8px",
            color: "var(--error)",
            marginBottom: "1.5rem"
          }}
        >
          {error}
        </div>
      )}

          {/* Filter Section - Always visible when there are leads */}
          {allLeads.length > 0 && (
            <div style={{ 
              marginBottom: "1.5rem", 
              padding: "1rem", 
              background: "rgba(139, 92, 246, 0.05)", 
              borderRadius: "12px",
              border: "2px solid var(--neutral-200)"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <FiFilter size={18} style={{ color: "var(--accent-purple)" }} />
                  <label style={{ fontSize: "0.875rem", fontWeight: "600", color: "var(--neutral-700)" }}>
                    Filter Leads:
                  </label>
                  {/* AI filtering disabled */}
                </div>
                <select
                  value={activeFilter}
                  onChange={(e) => {
                    setActiveFilter(e.target.value);
                    // Page reset is handled by useEffect
                  }}
                  style={{
                    padding: "0.5rem 1rem",
                    borderRadius: "8px",
                    border: "2px solid var(--accent-purple)",
                    background: "white",
                    color: "var(--primary-deep)",
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    fontWeight: "500",
                    minWidth: "200px"
                  }}
                >
                  {Object.entries(filterOptions).map(([key, option]) => (
                    <option key={key} value={key}>{option.label}</option>
                  ))}
                </select>
                {activeFilter !== 'default' && (
                  <button
                    onClick={() => {
                      setActiveFilter('default');
                      setCurrentPage(1);
                    }}
                    style={{
                      padding: "0.5rem 1rem",
                      borderRadius: "8px",
                      border: "2px solid var(--neutral-300)",
                      background: "white",
                      color: "var(--neutral-700)",
                      fontSize: "0.875rem",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem"
                    }}
                  >
                    <FiX size={16} />
                    Clear Filter
                  </button>
                )}
              </div>
              
              {activeFilter !== 'default' && (
                <div style={{ fontSize: "0.875rem", color: "var(--neutral-600)", marginTop: "0.75rem", display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                  <span>
                    Showing {filteredLeadsCount} lead{filteredLeadsCount !== 1 ? 's' : ''} matching your filter
                  </span>
                  {/* AI filtering disabled */}
                </div>
              )}
            </div>
          )}

          {/* Results */}
          {displayedLeads.length > 0 && (
            <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1.5rem",
              flexWrap: "wrap",
              gap: "1rem"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
              <h3 style={{ fontSize: "1.25rem", fontWeight: "600", color: "var(--primary-deep)", margin: 0 }}>
                Found {filteredLeadsCount} Lead{filteredLeadsCount !== 1 ? 's' : ''} 
                {currentPage > 1 && ` (Page ${currentPage})`}
              </h3>
              {displayedLeads.length > 0 && (
                <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={selectedLeads.size === displayedLeads.length && displayedLeads.length > 0}
                    onChange={toggleSelectAll}
                    style={{ width: "18px", height: "18px", cursor: "pointer" }}
                  />
                  <span style={{ fontSize: "0.875rem", color: "var(--neutral-600)" }}>
                    Select All ({selectedLeads.size} selected)
                  </span>
                </label>
              )}
            </div>
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
              {selectedLeads.size > 0 && (
                <button
                  onClick={transferToShortlisted}
                  className="btn-elegant btn-elegant-accent"
                  style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
                >
                  <FiCheck size={18} />
                  Transfer Selected ({selectedLeads.size})
                </button>
              )}
              {!isMobile && (
                <button
                  onClick={downloadCSV}
                  className="btn-elegant btn-elegant-secondary"
                  style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
                >
                  <FiDownload size={18} />
                  Download CSV
                </button>
              )}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gap: "1rem",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(400px, 1fr))",
              willChange: "contents",
              pointerEvents: modal.isOpen ? "none" : "auto"
            }}
          >
            {displayedLeads.map((lead, index) => (
              <div
                key={`${lead.placeId || 'lead'}-${index}`}
                style={{
                  background: "white",
                  border: selectedLeads.has(lead.placeId) ? "2px solid var(--accent-purple)" : "2px solid var(--neutral-200)",
                  borderRadius: "12px",
                  padding: "1.5rem",
                  transition: "all 0.3s",
                  boxShadow: selectedLeads.has(lead.placeId) ? "0 4px 20px rgba(139, 92, 246, 0.2)" : "0 2px 8px rgba(0, 0, 0, 0.05)",
                  willChange: "transform",
                  position: "relative"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--accent-purple)";
                  e.currentTarget.style.boxShadow = "0 4px 20px rgba(139, 92, 246, 0.15)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--neutral-200)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.05)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", marginBottom: "1rem" }}>
                  <input
                    type="checkbox"
                    checked={selectedLeads.has(lead.placeId)}
                    onChange={() => toggleLeadSelection(lead.placeId)}
                    style={{ 
                      width: "20px", 
                      height: "20px", 
                      cursor: "pointer",
                      marginTop: "0.25rem",
                      flexShrink: 0
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                      <h4
                        style={{
                          fontSize: "1.125rem",
                          fontWeight: "600",
                          color: "var(--primary-deep)",
                          lineHeight: "1.4",
                          flex: 1,
                          margin: 0
                        }}
                      >
                        {lead.name}
                      </h4>
                      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                        {contactedLeads.has(lead.placeId) && (
                          <span
                            style={{
                              background: "linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)",
                              color: "white",
                              padding: "0.25rem 0.75rem",
                              borderRadius: "12px",
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              flexShrink: 0
                            }}
                          >
                            Contacted
                          </span>
                        )}
                        {lead.priority >= 8 && (
                          <span
                            style={{
                              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                              color: "white",
                              padding: "0.25rem 0.75rem",
                              borderRadius: "12px",
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              flexShrink: 0
                            }}
                          >
                            High Priority
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {lead.phone && lead.phone !== 'Not available' && (
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
                      <FiPhone size={18} style={{ color: "var(--accent-purple)", marginTop: "0.125rem", flexShrink: 0 }} />
                      <a
                        href={`tel:${lead.phone.replace(/[^\d+]/g, '')}`}
                        onClick={() => markAsContacted(lead.placeId)}
                        style={{
                          color: "var(--accent-purple)",
                          textDecoration: "none",
                          fontWeight: "500",
                          wordBreak: "break-all",
                          fontSize: "1rem"
                        }}
                      >
                        {lead.phone}
                      </a>
                    </div>
                  )}

                  {lead.address && (
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
                      <FiMapPin size={18} style={{ color: "var(--accent-pink)", marginTop: "0.125rem", flexShrink: 0 }} />
                      <span style={{ color: "var(--neutral-700)", fontSize: "0.9375rem", lineHeight: "1.5" }}>
                        {lead.address}
                      </span>
                    </div>
                  )}
                </div>

                {/* Disqualify Button */}
                <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem" }}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      markAsDisqualified(lead);
                    }}
                    className="btn-elegant btn-elegant-secondary"
                    style={{
                      padding: "0.5rem 1rem",
                      fontSize: "0.875rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      flex: isMobile ? 1 : "none"
                    }}
                  >
                    <FiXCircle size={16} />
                    Disqualify
                  </button>
                </div>

                {isMobile && lead.phone && lead.phone !== 'Not available' && (
                  <div style={{ marginTop: "1rem" }}>
                    <a
                      href={`tel:${lead.phone.replace(/[^\d+]/g, '')}`}
                      onClick={() => markAsContacted(lead.placeId)}
                      className="btn-elegant btn-elegant-accent"
                      style={{
                        width: "100%",
                        textAlign: "center",
                        textDecoration: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem"
                      }}
                    >
                      <FiPhone size={18} />
                      Call
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {displayedLeads.length > 0 && totalPages > 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginTop: "2rem", alignItems: "center" }}>
              {/* Page Navigation */}
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", justifyContent: "center" }}>
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={!canGoPrevious || loading}
                  className="btn-elegant btn-elegant-secondary"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.5rem 1rem",
                    opacity: canGoPrevious ? 1 : 0.5,
                    cursor: canGoPrevious ? "pointer" : "not-allowed"
                  }}
                >
                  <FiChevronLeft size={18} />
                  Previous
                </button>

                {/* Page Numbers */}
                <div style={{ display: "flex", alignItems: "center", gap: "0.25rem", flexWrap: "wrap", justifyContent: "center" }}>
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 7) {
                      pageNum = i + 1;
                    } else if (currentPage <= 4) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 3) {
                      pageNum = totalPages - 6 + i;
                    } else {
                      pageNum = currentPage - 3 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        disabled={loading}
                        className="btn-elegant"
                        style={{
                          minWidth: "40px",
                          padding: "0.5rem",
                          background: currentPage === pageNum 
                            ? "var(--gradient-primary)" 
                            : "transparent",
                          color: currentPage === pageNum ? "white" : "var(--neutral-700)",
                          border: currentPage === pageNum 
                            ? "none" 
                            : "2px solid var(--neutral-300)",
                          fontWeight: currentPage === pageNum ? "600" : "500"
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={!canGoNext || loading}
                  className="btn-elegant btn-elegant-secondary"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.5rem 1rem",
                    opacity: canGoNext ? 1 : 0.5,
                    cursor: canGoNext ? "pointer" : "not-allowed"
                  }}
                >
                  Next
                  <FiChevronRight size={18} />
                </button>
              </div>

              {/* Page Info */}
              <div style={{ fontSize: "0.875rem", color: "var(--neutral-600)", textAlign: "center" }}>
                Page {currentPage} of {totalPages} • Showing {displayedLeads.length} of {filteredLeadsCount} lead{filteredLeadsCount !== 1 ? 's' : ''}
              </div>

              {/* Generate More Leads button - show when on last page and have search location */}
              {currentPage === totalPages && searchLocation && (
                <button
                  onClick={generateMoreLeads}
                  disabled={loading}
                  className="btn-elegant"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    minWidth: "200px",
                    background: "linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)",
                    color: "white",
                    border: "none",
                    marginTop: "0.5rem"
                  }}
                >
                  {loading ? (
                    <>
                      <span className="loading-elegant" style={{ borderTopColor: "white" }}></span>
                      Generating...
                    </>
                  ) : (
                    <>
                      Generate More Leads
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Show Generate More Leads button if only one page */}
          {displayedLeads.length > 0 && totalPages === 1 && searchLocation && (
            <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
              <button
                onClick={generateMoreLeads}
                disabled={loading}
                className="btn-elegant"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  minWidth: "200px",
                  background: "linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)",
                  color: "white",
                  border: "none"
                }}
              >
                {loading ? (
                  <>
                    <span className="loading-elegant" style={{ borderTopColor: "white" }}></span>
                    Generating...
                  </>
                ) : (
                  <>
                    Generate More Leads
                  </>
                )}
              </button>
            </div>
          )}

          {isMobile && displayedLeads.length > 0 && (
            <button
              onClick={downloadCSV}
              className="btn-elegant btn-elegant-secondary"
              style={{
                width: "100%",
                marginTop: "1.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem"
              }}
            >
              <FiDownload size={18} />
              Download CSV
            </button>
          )}
        </div>
      )}

      {!mapsReady && !error && (
        <div style={{ 
          textAlign: "center", 
          padding: "2rem", 
          color: "var(--neutral-600)",
          background: "rgba(139, 92, 246, 0.05)",
          borderRadius: "8px",
          marginTop: "1rem"
        }}>
          <p style={{ margin: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
            <span className="loading-elegant" style={{ borderTopColor: "var(--accent-purple)" }}></span>
            Loading Google Maps...
          </p>
        </div>
      )}

      {!loading && displayedLeads.length === 0 && searchQuery && !error && mapsReady && (
        <div style={{ textAlign: "center", padding: "3rem 2rem", color: "var(--neutral-600)" }}>
          <p>No leads found. Try searching for a different location or business.</p>
        </div>
      )}
    </div>
  );
}

